import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiCall, ApiError } from '../utils/apiHelpers';
import type { IndustryDoc, ProgramTypeCode, ProgramTypeConfig } from '../types';

export function IndustriesPage() {
  const [industries, setIndustries] = useState<IndustryDoc[]>([]);
  const [programTypes, setProgramTypes] = useState<ProgramTypeConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [programType, setProgramType] = useState<ProgramTypeCode>('other');
  const navigate = useNavigate();

  useEffect(() => {
    void reload();
  }, []);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [inds, pts] = await Promise.all([
        apiCall<IndustryDoc[]>('/api/industries'),
        apiCall<ProgramTypeConfig[]>('/api/program-types'),
      ]);
      setIndustries(inds);
      setProgramTypes(pts);
      const first = pts[0];
      if (first && !pts.find((p) => p.code === programType)) {
        setProgramType(first.code);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function create(ev: React.FormEvent) {
    ev.preventDefault();
    if (!name.trim()) return;
    setError(null);
    try {
      const created = await apiCall<IndustryDoc>('/api/industries', {
        method: 'POST',
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          programType,
        },
      });
      setCreating(false);
      setName('');
      setDescription('');
      void reload();
      navigate(`/industries/${created.id}/schemas`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0 }}>Programs</h1>
        {!creating && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            + New program
          </button>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fee', border: '1px solid #fcc', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {creating && (
        <form
          onSubmit={create}
          style={{
            border: '1px solid var(--border-color)',
            background: 'var(--card-bg)',
            padding: 20,
            borderRadius: 8,
            marginBottom: 24,
          }}
        >
          <h3 style={{ marginTop: 0 }}>New program</h3>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 4 }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>
              Program type *
            </label>
            <select
              value={programType}
              onChange={(e) => setProgramType(e.target.value as ProgramTypeCode)}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 4 }}
            >
              {programTypes.map((pt) => (
                <option key={pt.code} value={pt.code}>
                  {pt.displayName}
                  {pt.defaultFrameworks.length > 0
                    ? ` (defaults: ${pt.defaultFrameworks.join(', ')})`
                    : ''}
                </option>
              ))}
            </select>
            <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '4px 0 0' }}>
              Program type controls which compliance frameworks are pre-applied to new schemas.
            </p>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 4 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn--primary" disabled={!name.trim()}>
              Create
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setCreating(false);
                setName('');
                setDescription('');
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : industries.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>
          No programs yet. Create one to define a schema and start generating QR codes.
        </p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {industries.map((ind) => {
            const pt = programTypes.find((p) => p.code === ind.programType);
            return (
              <Link
                key={ind.id}
                to={`/industries/${ind.id}/schemas`}
                style={{
                  display: 'block',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 8,
                  padding: 16,
                  textDecoration: 'none',
                  color: 'inherit',
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8 }}>{ind.name}</h3>
                {ind.description && (
                  <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '4px 0' }}>{ind.description}</p>
                )}
                <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '8px 0 0' }}>
                  {pt?.displayName ?? ind.programType}
                  {ind.status === 'archived' ? ' · archived' : ''}
                </p>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
